import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const WatchlistManager = ({ onAddToWatchlist }) => {
  const [isAddingToken, setIsAddingToken] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTokens, setSelectedTokens] = useState([]);

  const popularTokens = [
    { symbol: 'ETH', name: 'Ethereum', price: 2456.78 },
    { symbol: 'ADA', name: 'Cardano', price: 0.4521 },
    { symbol: 'SOL', name: 'Solana', price: 98.34 },
    { symbol: 'DOT', name: 'Polkadot', price: 6.78 },
    { symbol: 'MATIC', name: 'Polygon', price: 0.8934 },
    { symbol: 'LINK', name: 'Chainlink', price: 14.56 },
    { symbol: 'UNI', name: 'Uniswap', price: 6.23 },
    { symbol: 'AVAX', name: 'Avalanche', price: 23.45 }
  ];

  const filteredTokens = popularTokens?.filter(token =>
    token?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    token?.symbol?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const handleAddToken = (token) => {
    if (!selectedTokens?.find(t => t?.symbol === token?.symbol)) {
      setSelectedTokens([...selectedTokens, token]);
    }
  };

  const handleRemoveToken = (symbol) => {
    setSelectedTokens(selectedTokens?.filter(t => t?.symbol !== symbol));
  };

  const handleSaveWatchlist = () => {
    selectedTokens?.forEach(token => onAddToWatchlist(token));
    setSelectedTokens([]);
    setIsAddingToken(false);
    setSearchQuery('');
  };

  return (
    <div className="bg-card border border-border rounded-brand p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Watchlist Manager</h3>
          <p className="text-sm text-muted-foreground">Add tokens to your personal watchlist</p>
        </div>
        <Button
          variant="outline"
          onClick={() => setIsAddingToken(!isAddingToken)}
          iconName={isAddingToken ? "X" : "Plus"}
          iconPosition="left"
        >
          {isAddingToken ? 'Cancel' : 'Add Tokens'}
        </Button>
      </div>
      {isAddingToken && (
        <div className="space-y-4 mb-6 p-4 bg-muted/50 rounded-brand-sm">
          <Input
            type="search"
            placeholder="Search cryptocurrencies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="mb-4"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-60 overflow-y-auto">
            {filteredTokens?.map((token) => {
              const isSelected = selectedTokens?.find(t => t?.symbol === token?.symbol);
              
              return (
                <div
                  key={token?.symbol}
                  className={`flex items-center justify-between p-3 rounded-brand-sm border cursor-pointer transition-brand ${
                    isSelected 
                      ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/70'
                  }`}
                  onClick={() => isSelected ? handleRemoveToken(token?.symbol) : handleAddToken(token)}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-foreground">
                        {token?.symbol?.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{token?.symbol}</p>
                      <p className="text-xs text-muted-foreground">{token?.name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-foreground">
                      ${token?.price?.toFixed(4)}
                    </p>
                    <Icon 
                      name={isSelected ? "Check" : "Plus"} 
                      size={16} 
                      className={isSelected ? "text-primary" : "text-muted-foreground"}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {selectedTokens?.length > 0 && (
            <div className="pt-4 border-t border-border">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-foreground">
                  Selected Tokens ({selectedTokens?.length})
                </p>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSaveWatchlist}
                  iconName="Save"
                  iconPosition="left"
                >
                  Add to Watchlist
                </Button>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {selectedTokens?.map((token) => (
                  <div
                    key={token?.symbol}
                    className="flex items-center space-x-2 bg-primary/10 text-primary px-3 py-1 rounded-brand-xs text-sm"
                  >
                    <span>{token?.symbol}</span>
                    <button
                      onClick={() => handleRemoveToken(token?.symbol)}
                      className="hover:text-destructive transition-brand"
                    >
                      <Icon name="X" size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      <div className="space-y-3">
        <h4 className="font-medium text-foreground">Quick Add Popular Tokens</h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {popularTokens?.slice(0, 4)?.map((token) => (
            <Button
              key={token?.symbol}
              variant="outline"
              size="sm"
              onClick={() => onAddToWatchlist(token)}
              className="justify-start"
            >
              <div className="w-6 h-6 bg-muted rounded-full flex items-center justify-center mr-2">
                <span className="text-xs font-bold">
                  {token?.symbol?.charAt(0)}
                </span>
              </div>
              {token?.symbol}
            </Button>
          ))}
        </div>
      </div>
      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center space-x-2">
            <Icon name="Info" size={16} />
            <span>Watchlist syncs across all devices</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Bell" size={16} />
            <span>Price alerts available</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WatchlistManager;